import React, { useState, useCallback } from 'react';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ImageDropzone = ({ 
  value, 
  onChange, 
  className = '',
  previewHeight = 'h-48',
  placeholder = 'Bild hierher ziehen oder klicken zum Hochladen'
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const handleUpload = async (file) => {
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Bitte nur Bilddateien hochladen');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Bild darf maximal 10MB groß sein');
      return;
    }

    setIsUploading(true);
    const token = localStorage.getItem('token');
    
    // Check if token exists
    if (!token) {
      toast.error('Nicht angemeldet. Bitte erneut einloggen.');
      setIsUploading(false);
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/upload`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      onChange(response.data.url);
      toast.success('Bild hochgeladen!');
    } catch (error) {
      console.error('Upload error:', error);
      if (error.response?.status === 401) {
        toast.error('Sitzung abgelaufen. Bitte erneut einloggen.');
        // Optional: Redirect to login
        localStorage.removeItem('token');
        window.location.href = '/admin/login';
      } else {
        toast.error(`Fehler beim Hochladen: ${error.response?.data?.detail || error.message}`);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleUpload(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    onChange('');
  };

  return (
    <div className={className}>
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !isUploading && document.getElementById(`dropzone-${placeholder}`)?.click()}
        className={`
          relative border-2 border-dashed rounded-lg transition-all cursor-pointer
          ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}
          ${isUploading ? 'pointer-events-none opacity-70' : ''}
          ${value ? '' : `${previewHeight} flex items-center justify-center`}
        `}
      >
        <input
          id={`dropzone-${placeholder}`}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
          disabled={isUploading}
        />

        {isUploading ? (
          <div className={`${previewHeight} flex flex-col items-center justify-center text-gray-500`}>
            <Loader2 className="w-8 h-8 animate-spin mb-2" />
            <span className="text-sm">Hochladen...</span>
          </div>
        ) : value ? (
          <div className="relative group">
            <img 
              src={value} 
              alt="Preview" 
              className={`w-full ${previewHeight} object-cover rounded-lg`}
            />
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-4">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  document.getElementById(`dropzone-${placeholder}`)?.click();
                }}
                className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                title="Bild ändern"
              >
                <Upload className="w-5 h-5 text-gray-700" />
              </button>
              <button
                onClick={handleRemove}
                className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                title="Bild entfernen"
              >
                <X className="w-5 h-5 text-red-500" />
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center text-gray-500 p-6">
            <div className={`p-3 rounded-full mb-3 ${isDragging ? 'bg-blue-100' : 'bg-gray-100'}`}>
              <ImageIcon className={`w-8 h-8 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
            </div>
            <p className="text-sm font-medium text-center">{placeholder}</p>
            <p className="text-xs text-gray-400 mt-1">PNG, JPG bis 10MB</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Multi-image dropzone for galleries
export const GalleryDropzone = ({ 
  images = [], 
  onChange,
  maxImages = 20,
  className = ''
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;

    const remainingSlots = maxImages - images.length;
    const filesToUpload = Array.from(files).slice(0, remainingSlots);

    if (filesToUpload.length === 0) {
      toast.error(`Maximal ${maxImages} Bilder erlaubt`);
      return;
    }

    setIsUploading(true);
    const token = localStorage.getItem('token');
    
    // Check if token exists
    if (!token) {
      toast.error('Nicht angemeldet. Bitte erneut einloggen.');
      setIsUploading(false);
      return;
    }
    
    const uploadedUrls = [];

    for (const file of filesToUpload) {
      if (!file.type.startsWith('image/')) continue;
      if (file.size > 10 * 1024 * 1024) continue;

      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await axios.post(
          `${BACKEND_URL}/api/upload`,
          formData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        uploadedUrls.push(response.data.url);
      } catch (error) {
        console.error('Upload error:', error);
        if (error.response?.status === 401) {
          toast.error('Sitzung abgelaufen. Bitte erneut einloggen.');
          localStorage.removeItem('token');
          window.location.href = '/admin/login';
          setIsUploading(false);
          return;
        }
      }
    }

    if (uploadedUrls.length > 0) {
      onChange([...images, ...uploadedUrls]);
      toast.success(`${uploadedUrls.length} Bild(er) hochgeladen!`);
    }

    setIsUploading(false);
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleUpload(e.dataTransfer.files);
  };

  const handleRemove = (index) => {
    const newImages = [...images];
    newImages.splice(index, 1);
    onChange(newImages);
  };

  return (
    <div className={className}>
      {/* Existing Images */}
      {images.length > 0 && (
        <div className="grid grid-cols-4 gap-3 mb-4">
          {images.map((img, idx) => (
            <div key={idx} className="relative group">
              <img 
                src={img} 
                alt={`Gallery ${idx + 1}`} 
                className="w-full h-24 object-cover rounded-lg border border-gray-200"
              />
              <button
                onClick={() => handleRemove(idx)}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Dropzone */}
      {images.length < maxImages && (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => !isUploading && document.getElementById('gallery-dropzone')?.click()}
          className={`
            border-2 border-dashed rounded-lg p-6 transition-all cursor-pointer text-center
            ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}
            ${isUploading ? 'pointer-events-none opacity-70' : ''}
          `}
        >
          <input
            id="gallery-dropzone"
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => handleUpload(e.target.files)}
            className="hidden"
            disabled={isUploading}
          />
          
          {isUploading ? (
            <div className="flex flex-col items-center">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400 mb-2" />
              <span className="text-sm text-gray-500">Hochladen...</span>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <Upload className={`w-8 h-8 mb-2 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
              <p className="text-sm text-gray-600">Bilder hierher ziehen</p>
              <p className="text-xs text-gray-400 mt-1">oder klicken zum Auswählen</p>
            </div>
          )}
        </div>
      )}

      <p className="text-xs text-gray-400 mt-2 text-right">
        {images.length} / {maxImages} Bilder
      </p>
    </div>
  );
};

export default ImageDropzone;
